import { variantOnSale } from '@/utils/shopify';
import { money, toAmount } from '@/utils/money';

export const formatRating = (ratingValue) => (ratingValue ? Number(ratingValue).toFixed(1) : null);

/**
 * Calculate the final price for a product, taking into account discounts
 * @param {Object} variant - The product variant
 * @param {Object} lineItem - The cart line item (optional, for discount calculations)
 * @returns {number} The calculated price
 */
export const calculateProductPrice = (variant, lineItem = null) => {
  const currency = variant.price?.currencyCode || 'EUR';
  const priceAmount = parseFloat(variant.price?.amount ?? variant.price);
  let price = money(priceAmount, currency);

  // Apply discount allocations if available
  if (lineItem?.discountAllocations?.length > 0) {
    const totalDiscount = lineItem.discountAllocations.reduce(
      (sum, curr) => sum.add(money(parseFloat(curr.discountedAmount.amount), currency)),
      money(0, currency)
    );

    const originalTotalPrice = price.multiply(lineItem.quantity);
    const discountedTotal = originalTotalPrice.subtract(totalDiscount);
    const discountedPricePerUnit = discountedTotal.divide(lineItem.quantity);

    price = discountedPricePerUnit.isPositive() ? discountedPricePerUnit : money(0, currency);
  }

  return toAmount(price);
};

/**
 * EEC representation of product
 * @param {Object} variant - The product variant
 * @param {Object} product - The product object
 * @param {number} quantity - The quantity
 * @param {string} imageUrl - The image URL (optional)
 * @param {Object} lineItem - The cart line item for discount calculations (optional)
 */
export const buildEECProduct = (variant, product, quantity, imageUrl = null, lineItem = null) => ({
  item_name: product.title,
  item_id: variant.sku,
  price: calculateProductPrice(variant, lineItem),
  item_brand: variantOnSale(variant),
  item_category: product.productType,
  item_variant: variant.title,
  quantity: quantity ?? 1,
  imageUrl,
});
